import React, { Component, Suspense } from 'react';
import classNames from 'classnames';
import { flowRight, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { resolveId } from '@wix/communities-blog-client-common';
import { EXPERIMENT_POST_PAGE_SCROLL_TO_COMMENTS } from '@wix/communities-blog-experiments';
import AnimatedLoader from '../../../common/components/animated-loader';
import AppLoaded from '../../../common/components/app-loaded';
import OnScreen from '../../../common/components/on-screen';
import Post from '../../../common/components/post';
import PrintWrapper from '../../../common/components/print-wrapper';
import { connect } from '../../../common/components/runtime-context';

import withAuth from '../../../common/hoc/with-auth';
import withCardBorderWidth from '../../../common/hoc/with-card-border-width';
import withDeviceType from '../../../common/hoc/with-device-type';
import withExperiment from '../../../common/hoc/with-experiment';
import withFeedMetadataSettings from '../../../common/hoc/with-feed-metadata-settings';
import withFontClassName from '../../../common/hoc/with-font-class-name';
import withTranslate from '../../../common/hoc/with-translate';
import { getRoute } from '../../../common/router/router-selectors';
import { getPostByIdOrSlug } from '../../../common/selectors/post-selectors';
import { isInPostPreview } from '../../../common/services/detect-route';
import { isInWix } from '../../../common/services/is-in-wix';
import scrollToContent from '../../../common/services/scroll-to-content';
import { resolvePostSlug } from '../../../common/services/slug';
import {
  getCommentId,
  isEditor,
  isSeo,
  isSSR,
} from '../../../common/store/basic-params/basic-params-selectors';
import {
  getIsPostLoaded,
  getIsRecentPostsLoaded,
} from '../../../common/store/is-loaded/is-loaded-selectors';
import PostPageLayout from '../../components/post-page-layout';
import ReadingTimeListener from '../../components/reading-time-listener/reading-time-listener';
import { POST_PAGE_COMMENTS_ON_SCREEN_THRESHOLD } from '../../constants/post-page';
import { getReadingSessionId } from '../../selectors/reading-session-id-selector';
import { PostPageWixComments } from './post-page-wix-comments-safe';
import RelevantPosts from './relevant-posts';
import styles from './post-page.scss';

export class PostPage extends Component {
  state = { isFetchingPost: false, forceRenderComments: false };
  commentsRef = React.createRef();

  componentDidMount() {
    this.blogPostViewTrackEvent();
  }

  blogPostViewTrackEvent = () => {
    const { post, trackEvent } = this.props;
    trackEvent('BlogPostView', {
      event: 'BlogPostView',
      eventCategory: 'Wix Blog',
      eventAction: 'BlogPostView',
      eventLabel: post.title,
      origin: 'Wix Blog',
    });
  };

  getPostContentContainer = () => {
    const post = document.querySelector('[data-hook="post"]');
    return post && post.getBoundingClientRect();
  };

  componentDidUpdate = ({ postSlug: prevPostSlug }) => {
    const { postSlug } = this.props;

    if (!postSlug || prevPostSlug === postSlug) {
      return;
    }

    scrollToContent(styles.postPage);
    this.blogPostViewTrackEvent();
  };

  reportReadingTime = (props) => {
    this.props.biPostScrolled({
      post_id: resolveId(this.props.post),
      is_demo: this.props.post.isDemo,
      ...props,
    });
  };

  onTabVisibilityChange = (props) => {
    this.props.biActiveTabChanged({
      post_id: resolveId(this.props.post),
      is_demo: this.props.post.isDemo,
      ...props,
    });
  };

  renderComments = () => {
    const {
      post,
      showComments,
      commentId,
      isSeo,
      isSSR,
      isExperimentScrollToCommentsEnabled,
    } = this.props;
    const canSeePost = post.canSeePaidContent !== false;
    const renderComments = !isSeo && canSeePost && showComments && post.id;
    if (!renderComments) {
      return null;
    }

    if (!isSSR && commentId) {
      this.props.initWixCommentsController();
      return (
        <div ref={this.commentsRef}>
          <Suspense fallback={<AnimatedLoader isLoading />}>
            <PostPageWixComments post={post} />
          </Suspense>
        </div>
      );
    }
    return (
      <OnScreen
        threshold={
          isExperimentScrollToCommentsEnabled
            ? POST_PAGE_COMMENTS_ON_SCREEN_THRESHOLD
            : undefined
        }
      >
        {({ isOnScreen, setRef }) => {
          if (!isOnScreen && !this.state.forceRenderComments) {
            return <div ref={setRef} />;
          }
          this.props.initWixCommentsController();
          return (
            <div ref={this.commentsRef}>
              <Suspense fallback={<AnimatedLoader isLoading />}>
                <PostPageWixComments post={post} />
              </Suspense>
            </div>
          );
        }}
      </OnScreen>
    );
  };

  render = () => {
    const {
      post,
      contentFontClassName,
      isPostLoaded,
      location,
      isSSR,
      showRecentPosts,
      showRelatedPosts,
      relatedPostsLabelKey,
      readingSessionId,
    } = this.props;

    return (
      <PostPageLayout
        className={classNames(styles.postPage, contentFontClassName)}
        data-hook="post-page"
      >
        {!isSSR && !isInWix() && (
          <ReadingTimeListener
            onTabVisibilityChange={(props) => this.onTabVisibilityChange(props)}
            readingSessionId={readingSessionId}
            onScroll={(props) => this.reportReadingTime(props)}
            getContentContainer={this.getPostContentContainer}
          />
        )}
        <AnimatedLoader isLoading={!isPostLoaded && isEmpty(post)}>
          {this.state.page > 1 ? null : (
            <div className={styles.post}>
              <PrintWrapper>
                <Post
                  key={resolveId(post)}
                  post={post}
                  location={location}
                  isInPostPage
                  onRatingsDisplayClick={() => {
                    this.setState.forceRenderComments
                      ? this.commentsRef?.current.scrollIntoView({
                          block: 'start',
                          behavior: 'smooth',
                        })
                      : this.setState({ forceRenderComments: true }, () =>
                          this.commentsRef?.current.scrollIntoView({
                            block: 'start',
                            behavior: 'smooth',
                          }),
                        );
                  }}
                />
              </PrintWrapper>
              <AppLoaded key={post.slug} />
            </div>
          )}
          <RelevantPosts
            post={post}
            showRecentPosts={showRecentPosts}
            showRelatedPosts={showRelatedPosts}
            relatedPostsLabelKey={relatedPostsLabelKey}
          />
          {this.renderComments()}
        </AnimatedLoader>
      </PostPageLayout>
    );
  };
}

PostPage.propTypes = {
  t: PropTypes.func,
  params: PropTypes.object,
  post: PropTypes.object.isRequired,
  postSlug: PropTypes.string,

  showComments: PropTypes.bool,

  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  isEditor: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPostLoaded: PropTypes.bool.isRequired,
  isRecentPostsLoaded: PropTypes.bool.isRequired,
  isSeo: PropTypes.bool.isRequired,

  location: PropTypes.object,

  borderWidth: PropTypes.number.isRequired,
  titleFontClassName: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  biPostScrolled: PropTypes.func.isRequired,
  biActiveTabChanged: PropTypes.func.isRequired,
  initWixCommentsController: PropTypes.func.isRequired,
  commentId: PropTypes.string,

  isExperimentScrollToCommentsEnabled: PropTypes.bool.isRequired,
};

const mapRuntimeToProps = (state, ownProps, actions) => {
  const postSlug = resolvePostSlug(ownProps.params);
  const useDraft = isInPostPreview(getRoute(state));
  let post = getPostByIdOrSlug(state, postSlug) || {};
  post = useDraft ? { ...post, ...(post.draft || {}) } : post;
  const postId = resolveId(post);
  return {
    post,
    postSlug,

    isEditor: isEditor(state),
    isPostLoaded: getIsPostLoaded(state, postSlug),
    isRecentPostsLoaded: getIsRecentPostsLoaded(state, postId),

    isSeo: isSeo(state),
    isSSR: isSSR(state),
    commentId: getCommentId(state),
    readingSessionId: getReadingSessionId(state),

    trackEvent: actions.trackEvent,
    biPostScrolled: actions.biPostScrolled,
    biActiveTabChanged: actions.biActiveTabChanged,
    initWixCommentsController: actions.initWixCommentsController,
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withFeedMetadataSettings,
  withDeviceType,
  withFontClassName,
  withCardBorderWidth,
  withAuth,
  withTranslate,
  withExperiment({
    isExperimentScrollToCommentsEnabled:
      EXPERIMENT_POST_PAGE_SCROLL_TO_COMMENTS,
  }),
)(PostPage);
